// =============================================================================
// Test Lab — BaseQL operations
// =============================================================================
// 4 operations:
//   - testLabRequestTestSession (mutation) — admin generates a one-shot token
//   - testLabListCallers       (query)    — UI dropdown list (admin only)
//   - testLabHistory           (query)    — view past test calls
//   - testLabSubscribeVerdicts (subscription) — live verdict stream
//
// Architecture: profile-based. Each profile pre-seeds the sessionData with
// real-looking CRM data so the PRODUCTION auth flow runs identically (same
// spell-back, confirmation, retry, GDPR). No more "scenario" granularity —
// the chosen profile's data determines which production path (A/B/C/existing)
// the LLM lands in.
// =============================================================================

import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { getContext } from '@adornis/baseql/server/context.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { logger } from '@adornis/base/logging.js';
import { map } from 'rxjs';
import { CallSession } from './CallSession.js';
import { FEEDBACK_STATUSES } from './feedback-status.js';
import {
  ALL_SCENARIOS,
  TEST_CALLERS,
  applyScenarioPreSeed,
  findTestCallerProfile,
  type TestScenario,
} from '../server/test-lab/test-callers.js';
import { createTestToken } from '../server/test-lab/test-token-store.js';
import { verdictsObservable } from '../server/test-lab/test-verdict-emitter.js';
import { liveAudioObservable } from '../server/test-lab/live-audio-bus.js';
import { runSystemHealth } from '../server/system-health.js';
import { invalidatePricing } from '../server/cost-tracking.js';
import { TestLabBoard } from './TestLabBoard.js';
import { PricingConfig } from './PricingConfig.js';
import { TestSpec } from './TestSpec.js';
import { RunResult } from './RunResult.js';
import { randomUUID } from 'node:crypto';
import { runTestSuite, personaForProfile, type RunTier, type TestCase } from '../server/test-lab/test-runner.js';
import { gradeAndPersistRunGroup } from '../server/test-lab/grade-and-persist.js';
import { beginRun, endRun, getRunStatus } from '../server/test-lab/run-status.js';
import { CallLog } from '@adornis/voice-assistant/call-logs.js';
import { CallRecording } from '@adornis/voice-assistant/call-recordings.js';
import { recordingPlayback, type RecordingPlayback } from './recording-playback.js';
import { CallVerdict } from './CallVerdict.js';
import { buildTurns, judgeCall, RULES } from '../server/call-judge.js';

// ─── auth helpers ─────────────────────────────────────────────────────────
function requireAuthFromContext(): string {
  const ctx = getContext();
  const userID = (ctx as any)?.userID;
  if (!userID) throw new Error('AUTH_REQUIRED: please sign in');
  return userID;
}

function getAdminAllowlist(): Set<string> {
  return new Set(
    (process.env.TEST_LAB_ADMIN_USER_IDS ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean),
  );
}

function requireTestLabAccess(): string {
  const userID = requireAuthFromContext();
  const allow = getAdminAllowlist();
  // Empty allowlist = dev convenience — any authenticated user allowed.
  if (allow.size > 0 && !allow.has(userID)) {
    throw new Error('TEST_LAB_NOT_ALLOWED: user not in TEST_LAB_ADMIN_USER_IDS');
  }
  return userID;
}

function isAdmin(userID: string): boolean {
  const allow = getAdminAllowlist();
  return allow.size === 0 || allow.has(userID);
}

// ─── testLabRequestTestSession (mutation) ─────────────────────────────────
// Admin requests a test session for a chosen profile.
// Returns a one-shot testToken to pass via WS meta.
export const testLabRequestTestSession = registerMutation({
  type: () => String,
  operationName: 'testLabRequestTestSession',
  resolve: (profileId: string, scenario: string, lang: string) => async () => {
    const userID = requireTestLabAccess();
    const profile = findTestCallerProfile(profileId);
    if (!profile) throw new Error(`UNKNOWN_PROFILE: ${profileId}`);
    const normalizedLang: 'de' | 'en' = lang === 'en' ? 'en' : 'de';
    const normalizedScenario: TestScenario = (ALL_SCENARIOS.find(s => s.id === scenario)?.id ??
      'full-flow') as TestScenario;
    const baseSeed = profile.preSeedForLang(normalizedLang);
    const preSeed = applyScenarioPreSeed(normalizedScenario, baseSeed, profile);
    const token = createTestToken({
      profileId,
      scenario: normalizedScenario,
      userID,
      preSeed,
    });
    logger.info(
      {
        userID,
        profileId,
        scenario: normalizedScenario,
        lang: normalizedLang,
        expectedFlow: profile.expectedFlow,
      },
      'Test Lab: token issued',
    );
    return token;
  },
  params: [
    { name: 'profileId', type: () => String },
    { name: 'scenario', type: () => String },
    { name: 'lang', type: () => String },
  ],
});

// ─── testLabListCallers (query) — UI dropdown + info card ──────────────────
// Returns JSON of full profile metadata including credentials the tester needs
// to read aloud during the call (phone, email, name, dossier numbers).
// Admin-only — credentials are mock but still test-internal.
export const testLabListCallers = registerQuery({
  type: () => String,
  operationName: 'testLabListCallers',
  resolve: () => async () => {
    requireTestLabAccess();
    return JSON.stringify(
      TEST_CALLERS.map(c => ({
        profileId: c.profileId,
        label: c.label,
        description: c.description,
        expectedFlow: c.expectedFlow,
        credentials: c.credentials,
      })),
    );
  },
  params: [],
});

// ─── testLabListScenarios (query) ─────────────────────────────────────────
// Returns JSON array of available test scenarios (id + label + description)
// so the UI can render a 2nd dropdown next to the profile picker.
export const testLabListScenarios = registerQuery({
  type: () => String,
  operationName: 'testLabListScenarios',
  resolve: () => async () => {
    requireTestLabAccess();
    return JSON.stringify(ALL_SCENARIOS);
  },
  params: [],
});

// ─── testLabHistory (query) ───────────────────────────────────────────────
// Returns past test calls. Non-admin users see only their own runs.
export const testLabHistory = registerQuery({
  type: () => [CallSession],
  operationName: 'testLabHistory',
  resolve: (profileId: string, limit: number) => async () => {
    const userID = requireAuthFromContext();
    const coll = await getCollection(CallSession);
    const query: any = { isTestCall: true };
    if (!isAdmin(userID)) {
      query.startedByUserID = userID; // enumeration leak guard
    }
    const p = (profileId ?? '').trim();
    if (p !== '') query.testCallerProfile = p;
    const cap = Math.min(Math.max(1, limit ?? 20), 100);
    const docs = await coll.find(query).sort({ startedAt: -1 }).limit(cap).toArray();
    return docs as unknown as CallSession[];
  },
  params: [
    { name: 'profileId', type: () => String },
    { name: 'limit', type: () => Int },
  ],
});

// ─── testLabGetFeedback (query) ──────────────────────────────────────────
// One existing call's editable note. Ordinary call reviewers do not need Test Lab access.
export const testLabGetFeedback = registerQuery({
  type: () => String,
  operationName: 'testLabGetFeedback',
  resolve: (callID: string) => async () => {
    requireAuthFromContext();
    const id = (callID ?? '').trim();
    if (!id) throw new Error('SESSION_ID_REQUIRED');
    const coll = await getCollection(CallSession);
    const visibleSession = await coll.findOne(
      { $or: [{ sessionID: id }, { callUuid: id }] },
      { projection: { _id: 0, sessionID: 1, callUuid: 1, testerName: 1, testerNotes: 1, testerNotesUpdatedAt: 1 } },
    );
    if (!visibleSession) throw new Error('FEEDBACK_NOT_FOUND');
    return JSON.stringify({
      sessionID: visibleSession.sessionID || visibleSession.callUuid || id,
      testerName: visibleSession.testerName ?? '',
      testerNotes: visibleSession.testerNotes ?? '',
      testerNotesUpdatedAt: visibleSession.testerNotesUpdatedAt ?? null,
    });
  },
  params: [{ name: 'callID', type: () => String }],
});

// ─── testLabSaveFeedback (mutation) ───────────────────────────────────────
// Saves/overwrites the tester feedback (testerName + testerNotes) for a
// specific CallSession. Idempotent: each save overwrites the previous note.
function normalizeFeedbackName(value: string): string {
  const name = (value ?? '').trim();
  if (!name) throw new Error('NAME_REQUIRED: please enter your name');
  if (name.length > 100) throw new Error('NAME_TOO_LONG: max 100 chars');
  if (/[\u0000-\u001f\u007f]/.test(name)) throw new Error('INVALID_NAME: control characters are not allowed');
  return name;
}

export const testLabSaveFeedback = registerMutation({
  type: () => Boolean,
  operationName: 'testLabSaveFeedback',
  resolve: (sessionID: string, testerName: string, testerNotes: string) => async () => {
    // Also used by /calls: ordinary testers may review calls without Test Lab privileges.
    requireAuthFromContext();
    const trimmedName = normalizeFeedbackName(testerName);
    const trimmedNotes = (testerNotes ?? '').trim();
    if (!sessionID || sessionID.trim() === '') {
      throw new Error('SESSION_ID_REQUIRED');
    }
    if (trimmedNotes.length === 0) {
      throw new Error('NOTES_REQUIRED: feedback text must not be empty');
    }
    if (trimmedNotes.length > 4000) {
      throw new Error('NOTES_TOO_LONG: max 4000 chars');
    }
    const coll = await getCollection(CallSession);
    // The frontend passes whatever `BrowserVoiceCall.start()` returns, which is
    // the WS-emitted callID — that maps to CallSession.callUuid, NOT sessionID.
    // Older code paths might still pass sessionID, so we match either field.
    const visibleSession = await coll.findOne({ $or: [{ sessionID }, { callUuid: sessionID }] });
    if (!visibleSession) {
      throw new Error(`SESSION_NOT_FOUND: no CallSession with sessionID or callUuid=${sessionID}`);
    }
    // Keep the authenticated read/publish filter, then write only validated feedback
    // to that exact row. This does not grant general CallSession modify permission.
    const feedbackCollection = await getRawCollection<EntityData<CallSession>>(CallSession._collectionName);
    const result = await feedbackCollection.updateOne(
      { _id: visibleSession._id },
      {
        $set: {
          testerName: trimmedName,
          testerNotes: trimmedNotes,
          testerNotesUpdatedAt: new Date(),
        },
      },
    );
    if (result.matchedCount === 0) {
      throw new Error(`SESSION_NOT_FOUND: no CallSession with sessionID or callUuid=${sessionID}`);
    }
    logger.info({ sessionID, testerName: trimmedName, notesLen: trimmedNotes.length }, 'Test Lab: feedback saved');
    return true;
  },
  params: [
    { name: 'sessionID', type: () => String },
    { name: 'testerName', type: () => String },
    { name: 'testerNotes', type: () => String },
  ],
});

// Status/deletion use the same authenticated visibility boundary as editing a note.
async function getVisibleFeedbackSession(sessionID: string) {
  const id = (sessionID ?? '').trim();
  if (!id) throw new Error('SESSION_ID_REQUIRED');
  const coll = await getCollection(CallSession);
  const session = await coll.findOne({ $or: [{ sessionID: id }, { callUuid: id }] });
  if (!session) throw new Error('FEEDBACK_NOT_FOUND');
  return session;
}

/** Reject changes racing between the permission-filtered read and the exact-row write. */
function feedbackSnapshotFilter(session: EntityData<CallSession>) {
  return {
    _id: session._id,
    testerName: session.testerName === undefined ? { $exists: false } : session.testerName,
    testerNotes: session.testerNotes === undefined ? { $exists: false } : session.testerNotes,
    testerNotesUpdatedAt:
      session.testerNotesUpdatedAt === undefined ? { $exists: false } : session.testerNotesUpdatedAt,
    testerFeedbackStatus:
      session.testerFeedbackStatus === undefined ? { $exists: false } : session.testerFeedbackStatus,
  };
}

export const testLabSetFeedbackStatus = registerMutation({
  type: () => Boolean,
  operationName: 'testLabSetFeedbackStatus',
  resolve: (sessionID: string, status: string) => async () => {
    requireAuthFromContext();
    // Accept primitive strings and compatible boxed String scalar values.
    const key =
      typeof status === 'string' || Object.prototype.toString.call(status) === '[object String]'
        ? String(status)
        : undefined;
    const nextStatus = FEEDBACK_STATUSES.find(item => item.value === key);
    if (!nextStatus) throw new Error('INVALID_FEEDBACK_STATUS');
    const session = await getVisibleFeedbackSession(sessionID);
    if (typeof session.testerNotes !== 'string' || !session.testerNotes.trim()) throw new Error('FEEDBACK_NOT_FOUND');
    const coll = await getRawCollection<EntityData<CallSession>>(CallSession._collectionName);
    const result = await coll.updateOne(feedbackSnapshotFilter(session), {
      $set: { testerFeedbackStatus: nextStatus.value },
    });
    if (result.matchedCount === 0) throw new Error('FEEDBACK_CHANGED');
    return true;
  },
  params: [
    { name: 'sessionID', type: () => String },
    { name: 'status', type: () => String },
  ],
});

export const testLabDeleteFeedback = registerMutation({
  type: () => Boolean,
  operationName: 'testLabDeleteFeedback',
  resolve: (sessionID: string) => async () => {
    requireAuthFromContext();
    const session = await getVisibleFeedbackSession(sessionID);
    const coll = await getRawCollection<EntityData<CallSession>>(CallSession._collectionName);
    const result = await coll.updateOne(feedbackSnapshotFilter(session), {
      $unset: { testerName: '', testerNotes: '', testerNotesUpdatedAt: '', testerFeedbackStatus: '' },
    });
    if (result.matchedCount === 0) throw new Error('FEEDBACK_CHANGED');
    return true;
  },
  params: [{ name: 'sessionID', type: () => String }],
});

// ─── systemHealth (mutation) ──────────────────────────────────────────────
// Runs the API-key / service health probes (Deepgram, Cartesia, OpenRouter,
// Azure, Twilio, MCP tunnel) and returns the result as a JSON string.
// Mutation (not query) so the UI can trigger it imperatively on a button click.
// Keys are read fresh from the .env file server-side and NEVER returned (masked).
export const systemHealth = registerMutation({
  type: () => String,
  operationName: 'systemHealth',
  resolve: (force: boolean) => async () => {
    requireTestLabAccess();
    const result = await runSystemHealth(!!force);
    return JSON.stringify(result);
  },
  params: [{ name: 'force', type: () => Boolean }],
});

// ─── Quick Board (shared links + SOS tasks) ───────────────────────────────
// Singleton document. testLabGetBoard returns the JSON blob; testLabSaveBoard
// upserts it. Shared across all testers — admin-gated, last-write-wins.
const BOARD_KEY = 'singleton';
const EMPTY_BOARD = '{"links":[],"tasks":[]}';

export const testLabGetBoard = registerQuery({
  type: () => String,
  operationName: 'testLabGetBoard',
  resolve: () => async () => {
    requireTestLabAccess();
    const coll = await getCollection(TestLabBoard);
    const doc = await coll.findOne({ boardKey: BOARD_KEY });
    return doc?.dataJSON ?? EMPTY_BOARD;
  },
  params: [],
});

export const testLabSaveBoard = registerMutation({
  type: () => Boolean,
  operationName: 'testLabSaveBoard',
  resolve: (dataJSON: string) => async () => {
    requireTestLabAccess();
    const raw = dataJSON ?? '';
    if (raw.length > 20000) throw new Error('BOARD_TOO_LARGE: max 20000 chars');
    let parsed: any;
    try {
      parsed = JSON.parse(raw || EMPTY_BOARD);
    } catch {
      throw new Error('INVALID_JSON');
    }
    // Normalize shape — only persist the two known arrays.
    const links = Array.isArray(parsed?.links) ? parsed.links : [];
    const tasks = Array.isArray(parsed?.tasks) ? parsed.tasks : [];
    const coll = await getCollection(TestLabBoard);
    await coll.updateOne(
      { boardKey: BOARD_KEY },
      { $set: { boardKey: BOARD_KEY, dataJSON: JSON.stringify({ links, tasks }), updatedAt: new Date() } },
      { upsert: true },
    );
    logger.info({ links: links.length, tasks: tasks.length }, 'Test Lab: board saved');
    return true;
  },
  params: [{ name: 'dataJSON', type: () => String }],
});

// ─── Pricing Config (UI-editable per-unit cost rates) ─────────────────────
// Singleton. getPricingConfig returns {rates,updatedBy,updatedAt} JSON; save
// validates + upserts + invalidates the cost-tracking cache. Admin-gated.
const PRICING_KEY = 'singleton';
const DEFAULT_PRICING_JSON = JSON.stringify({
  llm: { openrouter: { inPerM: 2.0, outPerM: 6.0 }, azure: { inPerM: 2.0, outPerM: 6.0 } },
  cartesia: { usdPer1MChars: 65.0 },
  deepgram: { usdPerMin: 0.0077 },
  twilio: { usdPerMin: 0.014 },
  usdToEur: 0.92,
});

/** Every numeric leaf must be finite, > 0, and within a sane ceiling. Throws on bad input. */
function validatePricingLeaves(o: any, path = ''): void {
  for (const [k, v] of Object.entries(o ?? {})) {
    const p = path ? `${path}.${k}` : k;
    if (v != null && typeof v === 'object') {
      validatePricingLeaves(v, p);
      continue;
    }
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) throw new Error(`INVALID_RATE: ${p} must be a finite number`);
    if (n <= 0) throw new Error(`INVALID_RATE: ${p} must be > 0`);
    if (n > 100000) throw new Error(`RATE_TOO_HIGH: ${p}`);
  }
}

export const getPricingConfig = registerQuery({
  type: () => String,
  operationName: 'getPricingConfig',
  resolve: () => async () => {
    requireTestLabAccess();
    const coll = await getCollection(PricingConfig);
    const doc = await coll.findOne({ configKey: PRICING_KEY });
    if (doc?.dataJSON) {
      return JSON.stringify({
        rates: JSON.parse(doc.dataJSON),
        updatedBy: doc.updatedBy ?? null,
        updatedAt: doc.updatedAt ?? null,
      });
    }
    // seed-on-first-read
    await coll.updateOne(
      { configKey: PRICING_KEY },
      {
        $set: {
          configKey: PRICING_KEY,
          dataJSON: DEFAULT_PRICING_JSON,
          updatedBy: 'system:seed',
          updatedAt: new Date(),
        },
      },
      { upsert: true },
    );
    return JSON.stringify({ rates: JSON.parse(DEFAULT_PRICING_JSON), updatedBy: 'system:seed', updatedAt: null });
  },
  params: [],
});

export const savePricingConfig = registerMutation({
  type: () => Boolean,
  operationName: 'savePricingConfig',
  resolve: (dataJSON: string) => async () => {
    const userID = requireTestLabAccess();
    const raw = dataJSON ?? '';
    if (raw.length > 8000) throw new Error('PRICING_TOO_LARGE: max 8000 chars');
    let parsed: any;
    try {
      parsed = JSON.parse(raw || '{}');
    } catch {
      throw new Error('INVALID_JSON');
    }
    validatePricingLeaves(parsed);
    const coll = await getCollection(PricingConfig);
    await coll.updateOne(
      { configKey: PRICING_KEY },
      { $set: { configKey: PRICING_KEY, dataJSON: JSON.stringify(parsed), updatedBy: userID, updatedAt: new Date() } },
      { upsert: true },
    );
    invalidatePricing(); // refresh the cost-tracking cache immediately
    logger.info({ userID }, 'Pricing config saved');
    return true;
  },
  params: [{ name: 'dataJSON', type: () => String }],
});

// ─── testLabListFeedback (query) ──────────────────────────────────────────
// Returns CallSessions that have testerNotes set, sorted by most recent
// feedback timestamp. Optional filter by testerName.
export const testLabListFeedback = registerQuery({
  type: () => [CallSession],
  operationName: 'testLabListFeedback',
  resolve: (testerName: string, limit: number) => async (_gqlFields?: BaseQLSelectionSet<CallSession>) => {
    requireTestLabAccess();
    const coll = await getCollection(CallSession);
    const query: any = { testerNotes: { $exists: true, $ne: '' } };
    const t = (testerName ?? '').trim();
    if (t !== '') {
      const name = normalizeFeedbackName(t);
      // Literal, case-insensitive lookup also finds the lowercase legacy names.
      query.testerName = new RegExp('^' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$', 'i');
    }
    const cap = Math.min(Math.max(1, limit ?? 50), 200);
    const docs = await coll.find(query).sort({ testerNotesUpdatedAt: -1 }).limit(cap).toArray();
    return docs as unknown as CallSession[];
  },
  params: [
    { name: 'testerName', type: () => String },
    { name: 'limit', type: () => Int },
  ],
});

// ─── testLabSubscribeVerdicts (subscription) ──────────────────────────────
export const testLabSubscribeVerdicts = registerSubscription({
  type: () => String,
  operationName: 'testLabSubscribeVerdicts',
  resolve: (sessionID: string) => () => {
    requireAuthFromContext();
    return verdictsObservable(sessionID).pipe(map(v => JSON.stringify(v)));
  },
  params: [{ name: 'sessionID', type: () => String }],
});

// =============================================================================
// NDI Voice-Testing suite — UI-shell resolvers (Round A)
//   List/sign-off specs, request a (manual) run token, subscribe verdicts,
//   list run results. Server-driven run (ndiSuiteRunSuite) + graders land later.
//   List ops return JSON strings (no selection-set needed, mirrors testLabListCallers).
// =============================================================================

export const ndiSuiteListSpecs = registerQuery({
  type: () => String,
  operationName: 'ndiSuiteListSpecs',
  resolve: () => async () => {
    requireTestLabAccess();
    const c = await getCollection(TestSpec);
    const docs = await c.find({ isLatest: true }).sort({ versionedAt: -1 }).limit(200).toArray();
    return JSON.stringify(
      docs.map((d: any) => {
        let signedOffBy: string | undefined;
        try {
          const so = JSON.parse(d.signOffsJSON || '[]');
          if (Array.isArray(so) && so.length) signedOffBy = so[so.length - 1]?.byUserID;
        } catch {}
        return {
          specKey: d.specKey,
          version: d.version,
          name: d.name,
          description: d.description,
          personaProfileId: d.personaProfileId,
          signedOffBy,
        };
      }),
    );
  },
  params: [],
});

export const ndiSuiteSignOff = registerMutation({
  type: () => Boolean,
  operationName: 'ndiSuiteSignOff',
  resolve: (specKey: string, version: number, expectedHash: string, note: string) => async () => {
    const userID = requireTestLabAccess();
    const key = (specKey ?? '').trim();
    if (!key) throw new Error('SPEC_KEY_REQUIRED');
    if (!Number.isInteger(version) || version <= 0) throw new Error('VERSION_REQUIRED');
    const c = await getCollection(TestSpec);
    const doc: any = await c.findOne({ specKey: key, version });
    if (!doc) throw new Error('SPEC_NOT_FOUND');
    if ((expectedHash ?? '').trim() && expectedHash !== doc.specHash)
      throw new Error('HASH_MISMATCH: reload before signing');
    let existing: any[] = [];
    try {
      existing = JSON.parse(doc.signOffsJSON || '[]');
    } catch {}
    if (existing.some(s => s.byUserID === userID && s.specHash === doc.specHash)) return true;
    const record = {
      byUserID: userID,
      atISO: new Date().toISOString(),
      specHash: doc.specHash,
      version,
      note: (note ?? '').trim() || undefined,
    };
    const res = await c.updateOne(
      { _id: doc._id, specHash: doc.specHash },
      { $set: { signOffsJSON: JSON.stringify([...existing, record]) } },
    );
    if (res.matchedCount === 0) throw new Error('SIGN_OFF_RACE: retry');
    return true;
  },
  params: [
    { name: 'specKey', type: () => String },
    { name: 'version', type: () => Int },
    { name: 'expectedHash', type: () => String },
    { name: 'note', type: () => String },
  ],
});

export const ndiSuiteRequestRun = registerMutation({
  type: () => String,
  operationName: 'ndiSuiteRequestRun',
  resolve: (lang: string, profileId: string, scenario: string) => async () => {
    const userID = requireTestLabAccess();
    const pid = (profileId ?? '').trim();
    if (!pid) throw new Error('PROFILE_REQUIRED');
    const profile = findTestCallerProfile(pid);
    if (!profile) throw new Error(`UNKNOWN_PROFILE: ${pid}`);
    const normalizedLang: 'de' | 'en' = lang === 'en' ? 'en' : 'de';
    const normalizedScenario: TestScenario = (ALL_SCENARIOS.find(s => s.id === scenario)?.id ??
      'full-flow') as TestScenario;
    const baseSeed = profile.preSeedForLang(normalizedLang);
    const preSeed = applyScenarioPreSeed(normalizedScenario, baseSeed, profile);
    return createTestToken({ profileId: pid, scenario: normalizedScenario, userID, preSeed });
  },
  params: [
    { name: 'lang', type: () => String },
    { name: 'profileId', type: () => String },
    { name: 'scenario', type: () => String },
  ],
});

export const ndiSuiteSubscribeVerdicts = registerSubscription({
  type: () => String,
  operationName: 'ndiSuiteSubscribeVerdicts',
  resolve: (sessionID: string) => () => {
    requireAuthFromContext();
    return verdictsObservable(sessionID).pipe(map(v => JSON.stringify(v)));
  },
  params: [{ name: 'sessionID', type: () => String }],
});

// Live audio: stream both legs' PCM frames (base64) of a running test call to the
// Run & Live UI for real-time listen-in. Mirrors the verdict subscription; the
// frames are tee'd by the synthetic caller into the in-memory live-audio bus.
export const ndiSuiteLiveAudio = registerSubscription({
  type: () => String,
  operationName: 'ndiSuiteLiveAudio',
  resolve: (callID: string) => () => {
    requireTestLabAccess();
    return liveAudioObservable((callID ?? '').trim()).pipe(map(f => JSON.stringify(f)));
  },
  params: [{ name: 'callID', type: () => String }],
});

export const ndiSuiteListRuns = registerQuery({
  type: () => String,
  operationName: 'ndiSuiteListRuns',
  resolve: (limit: number) => async () => {
    const userID = requireAuthFromContext();
    const c = await getCollection(RunResult);
    const q: any = {};
    if (!isAdmin(userID)) q.startedByUserID = userID;
    const cap = Math.min(Math.max(1, limit ?? 20), 100);
    const docs = await c.find(q).sort({ createdAt: -1 }).limit(cap).toArray();
    return JSON.stringify(
      docs.map((d: any) => ({
        runGroupID: d.runGroupID,
        createdAt: d.createdAt,
        gatePassed: d.gatePassed,
        passes: d.passes,
        totalRuns: d.totalRuns,
        wilsonLower: d.wilsonLower,
        perRun: d.perRunRowsJSON,
      })),
    );
  },
  params: [{ name: 'limit', type: () => Int }],
});

// ─── ndiSuiteCallTranscript (query) — conversation + recording for one call ───
// Returns the turn-by-turn dialogue (caller robot vs agent) + the recording URL,
// so the UI can show/play what was said.
export const ndiSuiteCallTranscript = registerQuery({
  type: () => String,
  operationName: 'ndiSuiteCallTranscript',
  resolve: (callID: string) => async () => {
    requireTestLabAccess();
    const id = (callID ?? '').trim();
    if (!id) throw new Error('CALLID_REQUIRED');
    const logColl = await getCollection(CallLog);
    const rows: any[] = await logColl.find({ callID: id }).sort({ timestamp: 1, _id: 1 }).toArray();
    const turns: any[] = [];
    for (const r of rows) {
      if (r.type !== 'message') continue;
      let p: any;
      try {
        p = r.payload ? JSON.parse(r.payload) : undefined;
      } catch {
        p = undefined;
      }
      if (!p) continue;
      const ts = r.timestamp ? new Date(r.timestamp).getTime() : 0;
      const content = typeof p.content === 'string' ? p.content.trim() : '';
      if (p.role === 'assistant' && content) turns.push({ ts, role: 'agent', text: content });
      else if (p.role === 'assistant' && Array.isArray(p.tool_calls) && p.tool_calls.length)
        turns.push({
          ts,
          role: 'tool',
          text:
            '⚙ ' +
            p.tool_calls
              .map((t: any) => t.function?.name)
              .filter(Boolean)
              .join(', '),
        });
      else if (p.role === 'user' && content && !/^\[CALL_(STARTED|ENDED)\]/.test(content))
        turns.push({ ts, role: 'caller', text: content });
      else if (p.role === 'tool' && content) turns.push({ ts, role: 'tool', text: '↳ ' + content.slice(0, 160) });
    }
    let playback: RecordingPlayback = { state: 'missing', url: null };
    try {
      const recColl = await getCollection(CallRecording);
      const rec: any = await recColl.findOne({ callID: id }, { sort: { createdAt: -1, _id: -1 } } as any);
      playback = recordingPlayback(rec);
    } catch {}
    return JSON.stringify({
      callID: id,
      turns,
      recordingUrl: playback.url,
      recordingState: playback.state,
    });
  },
  params: [{ name: 'callID', type: () => String }],
});

// ─── ndiSuitePersonaForProfile (query) — default editable playbook text ───────
// Returns the auto-built caller persona ("playbook") for a profile so the
// front-end can pre-fill the editable textarea in Run & Live. The user may edit
// it and pass it back as case.persona to ndiSuiteRunSuite.
export const ndiSuitePersonaForProfile = registerQuery({
  type: () => String,
  operationName: 'ndiSuitePersonaForProfile',
  resolve: (profileId: string, lang: string) => async () => {
    requireTestLabAccess();
    const pid = (profileId ?? '').trim();
    if (!pid) throw new Error('PROFILE_REQUIRED');
    const normalizedLang: 'de' | 'en' = lang === 'en' ? 'en' : 'de';
    const persona = personaForProfile(pid, normalizedLang);
    if (!persona) throw new Error(`UNKNOWN_PROFILE: ${pid}`);
    return persona;
  },
  params: [
    { name: 'profileId', type: () => String },
    { name: 'lang', type: () => String },
  ],
});

// ─── ndiSuiteRunStatus (query) — live status of an in-flight run ───────────────
// Polled by Run & Live to stream the active call's transcript BEFORE the run /
// grading finishes. Returns {runGroupID, activeCallID, callIDs[], done}.
export const ndiSuiteRunStatus = registerQuery({
  type: () => String,
  operationName: 'ndiSuiteRunStatus',
  resolve: (runGroupID: string) => async () => {
    requireTestLabAccess();
    const id = (runGroupID ?? '').trim();
    if (!id) throw new Error('RUNGROUP_REQUIRED');
    const s = getRunStatus(id);
    if (!s) return JSON.stringify({ runGroupID: id, activeCallID: null, callIDs: [], done: false, known: false });
    return JSON.stringify({
      runGroupID: s.runGroupID,
      activeCallID: s.activeCallID,
      callIDs: s.callIDs,
      done: s.done,
      known: true,
    });
  },
  params: [{ name: 'runGroupID', type: () => String }],
});

// ─── ndiSuiteRunSuite (mutation) — server-driven automated run ────────────────
// The synthetic caller drives the agent (k×), graders score by callID, one
// RunResult is upserted. Returns a JSON summary. Smoke tier (k=1) is ~30-60s.
export const ndiSuiteRunSuite = registerMutation({
  type: () => String,
  operationName: 'ndiSuiteRunSuite',
  resolve: (casesJSON: string, tier: string, specKey: string) => async () => {
    const startedByUserID = requireTestLabAccess();
    let cases: TestCase[] = [];
    if ((casesJSON ?? '').length > 100_000) throw new Error('CASES_TOO_LARGE');
    try {
      cases = JSON.parse(casesJSON || '[]');
    } catch {
      throw new Error('INVALID_JSON');
    }
    if (!Array.isArray(cases) || cases.length === 0) throw new Error('NO_CASES');
    const runTier: RunTier = (['smoke', 'regression', 'release'] as const).includes(tier as any)
      ? (tier as RunTier)
      : 'smoke';

    const runGroupID = randomUUID();
    logger.info({ runGroupID, tier: runTier, cases: cases.length, startedByUserID }, 'NDI suite: run starting (async)');
    beginRun(runGroupID, Date.now()); // mark live so Run & Live can poll immediately
    // Fire-and-forget: a run + grading can take >60s — longer than the BaseQL op
    // timeout. Return immediately; the RunResult appears in ndiSuiteListRuns when done.
    void (async () => {
      try {
        const dto = await runTestSuite(cases, { startedByUserID, tier: runTier, runGroupID });
        logger.info(
          { runGroupID, issued: dto.totals.issued, ok: dto.totals.ok, failed: dto.totals.failed },
          'NDI suite: calls done — grading',
        );
        if (dto.allCallIDs.length > 0) {
          await gradeAndPersistRunGroup({
            runGroupID,
            callIDs: dto.allCallIDs,
            startedByUserID,
            tier: runTier,
            scenarioName: cases[0]?.label,
            callerProfile: cases[0]?.profileId,
            nowISO: new Date().toISOString(),
          });
        } else {
          logger.warn({ runGroupID }, 'NDI suite: no callIDs produced — nothing to grade');
        }
      } catch (err) {
        logger.error({ err, runGroupID }, 'NDI suite: background run failed');
      } finally {
        endRun(runGroupID, Date.now()); // calls done → Run & Live switches to Results
      }
    })();
    return JSON.stringify({ runGroupID, status: 'started' });
  },
  params: [
    { name: 'casesJSON', type: () => String },
    { name: 'tier', type: () => String },
    { name: 'specKey', type: () => String },
  ],
});

// ═════════════════════════════════════════════════════════════════════════════
// Call Judge — audit a finished call against its own instructions
// ═════════════════════════════════════════════════════════════════════════════
// Manual-trigger first, on purpose. Running it on every call is one line away, but a judge you
// have not yet calibrated is a noise generator: it must be pointed at calls whose verdict we
// already know before it is allowed to speak about calls we do not.

/** Audit ONE call. Returns the full verdict as JSON and stores it. */
export const zwJudgeCall = registerMutation({
  type: () => String,
  operationName: 'zwJudgeCall',
  resolve: (callID: string) => async () => {
    requireTestLabAccess();
    const id = (callID ?? '').trim();
    if (!id) throw new Error('CALLID_REQUIRED');
    const verdict = await judgeCall(id);
    logger.info(
      { callID: id, severity: verdict.severity, findings: verdict.findings.length, ms: verdict.elapsedMs },
      'call-judge: verdict',
    );
    return JSON.stringify(verdict);
  },
  params: [{ name: 'callID', type: () => String }],
});

/** Audit the most recent N calls that have no verdict yet. */
export const zwJudgeRecent = registerMutation({
  type: () => String,
  operationName: 'zwJudgeRecent',
  resolve: (limit: number) => async () => {
    requireTestLabAccess();
    const n = Math.min(Math.max(Number(limit) || 5, 1), 25);
    const logColl = await getCollection(CallLog);
    // distinct() is typed Maybe<string>[]; drop the empties rather than casting the type away.
    const ids = ((await logColl.distinct('callID', {})) as (string | null | undefined)[]).filter(
      (x): x is string => typeof x === 'string' && x.length > 0,
    );
    // distinct() has no order, so rank by the newest row each call has.
    const withTs: { id: string; ts: number }[] = [];
    for (const id of ids) {
      if (!id) continue;
      const last: any = await logColl.findOne({ callID: id }, { sort: { timestamp: -1 } } as any);
      withTs.push({ id, ts: last?.timestamp ? new Date(last.timestamp).getTime() : 0 });
    }
    withTs.sort((a, b) => b.ts - a.ts);

    const verColl = await getCollection(CallVerdict);
    const out: any[] = [];
    for (const c of withTs) {
      if (out.length >= n) break;
      const already = await verColl.findOne({ callID: c.id });
      if (already) continue;
      const v = await judgeCall(c.id);
      out.push({ callID: v.callID, severity: v.severity, findings: v.findings.length, error: v.error ?? null });
    }
    return JSON.stringify({ judged: out.length, calls: out });
  },
  params: [{ name: 'limit', type: () => Int }],
});

/** Recent verdicts, worst first, for the Test Lab tab. */
export const zwJudgeList = registerQuery({
  type: () => String,
  operationName: 'zwJudgeList',
  resolve: (limit: number) => async () => {
    requireTestLabAccess();
    const n = Math.min(Math.max(Number(limit) || 50, 1), 200);
    const coll = await getCollection(CallVerdict);
    const rows: any[] = await coll.find({}).sort({ judgedAt: -1 }).limit(n).toArray();
    // Newest CALL first, not worst verdict first. The severity is already carried by the colour,
    // and the question someone actually arrives with is "how did the one I just made go?" —
    // sorting by call time answers it without scrolling. Re-judging an old call must not make it
    // jump to the top either, which is why this is callStartedAt and not judgedAt.
    const when = (v: any) => new Date(v?.callStartedAt ?? v?.judgedAt ?? 0).getTime() || 0;
    rows.sort((a, b) => when(b) - when(a));
    return JSON.stringify({
      // Empty by default, which makes the link relative — so it points at the call overview of
      // whichever environment is serving this page. On staging that is exactly
      // https://zwickroell.staging.new-digital-intelligence.com/calls/<id>. Set
      // ZW_CALL_LINK_BASE to an absolute origin to aim it somewhere else on purpose.
      callLinkBase: (process.env.ZW_CALL_LINK_BASE ?? '').replace(/\/+$/, ''),
      rules: RULES.map(x => ({ id: x.id, severity: x.severity, text: x.text })),
      verdicts: rows.map(v => ({
        callID: v.callID,
        judgedAt: v.judgedAt,
        flags: (() => {
          try {
            return JSON.parse(v.flagsJSON ?? '{}');
          } catch {
            return {};
          }
        })(),
        callStartedAt: v.callStartedAt ?? null,
        callEndedAt: v.callEndedAt ?? null,
        callDurationMs: v.callDurationMs ?? null,
        path: v.path,
        pathJudged: v.pathJudged,
        severity: v.severity,
        findingCount: v.findingCount,
        turnCount: v.turnCount,
        elapsedMs: v.elapsedMs,
        error: v.error ?? null,
        findings: (() => {
          try {
            return JSON.parse(v.findingsJSON ?? '[]');
          } catch {
            return [];
          }
        })(),
      })),
    });
  },
  params: [{ name: 'limit', type: () => Int }],
});

/**
 * The compacted turn list for one call — exactly what the judge was shown, nothing more.
 *
 * Rebuilt on demand rather than stored on the verdict: the log is the source of truth, a copy
 * inside the verdict would be a second place for the same transcript to live (and to leak), and
 * re-deriving it costs one indexed query.
 */
export const zwJudgeTranscript = registerQuery({
  type: () => String,
  operationName: 'zwJudgeTranscript',
  resolve: (callID: string) => async () => {
    requireTestLabAccess();
    const id = (callID ?? '').trim();
    if (!id) throw new Error('CALLID_REQUIRED');
    const turns = await buildTurns(id);
    return JSON.stringify({ callID: id, turns });
  },
  params: [{ name: 'callID', type: () => String }],
});
